import { useRef, useState } from 'react'
import { Button, Menu, Popover } from '@highlight/ui'
import { Check, Send } from 'lucide-react'
import { useGlobalStore } from '@highlight/ipc/react'
import { ViewSearchInput } from '../../../components/view-search-input'
import { View } from '../../../components/view'
import { ViewFilterMenu, type ViewFilterOption } from '../../../components/view-filter-menu'
import { AUTO_CLEAR_OPTIONS } from '../../../constants'
import { useClearAllStagedActions } from '../../../hooks/use-staged-actions'
import type { ActionsViewModel, ActionFilterType } from '../use-actions-view-model'

const FILTER_OPTIONS: Array<ViewFilterOption<ActionFilterType>> = [
  { value: 'staged', label: 'Drafts' },
  { value: 'all', label: 'All' },
  { value: 'completed', label: 'Completed' },
  { value: 'archived', label: 'Archived' },
]

/**
 * Fixed view header per the Outbox design (`7652:21910`): the title lockup with
 * a single filter chip, and the search pill underneath — no tab bar.
 *
 * The auto-archive interval and "Archive all" hang off the bottom of that same
 * menu. The design doesn't draw them, but they have no other home in the app,
 * and a list-scoped control belongs with the list's other control rather than
 * as a second row of chrome.
 */
export function ActionsViewHeader({ model }: { model: ActionsViewModel }) {
  const { archiveAllStagedActions } = useClearAllStagedActions()
  const [autoClearInterval, setAutoClearInterval] = useGlobalStore<string>(
    'settings',
    'autoClearActions.interval',
    '72h',
  )
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false)
  const archiveAnchorRef = useRef<HTMLDivElement>(null)

  return (
    <View.Header
      icon={<Send />}
      title="Ready for you"
      accessory={
        <div ref={archiveAnchorRef}>
          <ViewFilterMenu value={model.filter} options={FILTER_OPTIONS} subject="outbox" onChange={model.setFilter}>
            <Menu.Separator />
            <Menu.Group>
              <Menu.GroupLabel>Archive after</Menu.GroupLabel>
              <Menu.RadioGroup value={autoClearInterval} onValueChange={(v) => setAutoClearInterval(v as string)}>
                {AUTO_CLEAR_OPTIONS.map((option) => (
                  <Menu.RadioItem key={option.value} value={option.value}>
                    <Menu.RadioItemIndicator>
                      <Check className="size-3" />
                    </Menu.RadioItemIndicator>
                    {option.label}
                  </Menu.RadioItem>
                ))}
              </Menu.RadioGroup>
            </Menu.Group>
            <Menu.Separator />
            <Menu.Item onClick={() => setIsArchiveDialogOpen(true)}>Archive all</Menu.Item>
          </ViewFilterMenu>

          <Popover open={isArchiveDialogOpen} onOpenChange={setIsArchiveDialogOpen}>
            <Popover.Portal>
              <Popover.Positioner anchor={archiveAnchorRef} side="bottom" align="end" sideOffset={8}>
                <Popover.Popup className="w-56">
                  <p className="text-foreground mb-1 text-sm font-medium">Archive all actions?</p>
                  <p className="text-foreground-secondary type-ui-xs mb-3">
                    This will archive all {model.stagedCount} actions.
                  </p>
                  <div className="flex items-center justify-end gap-2">
                    <Button variant="outline" onClick={() => setIsArchiveDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      tone="danger"
                      onClick={() => {
                        archiveAllStagedActions()
                        setIsArchiveDialogOpen(false)
                      }}
                    >
                      Archive
                    </Button>
                  </div>
                </Popover.Popup>
              </Popover.Positioner>
            </Popover.Portal>
          </Popover>
        </div>
      }
    >
      <ViewSearchInput
        value={model.searchQuery}
        onChange={model.setSearchQuery}
        placeholder={model.searchPlaceholder}
        label="Search actions"
      />
    </View.Header>
  )
}
